/* eslint-disable @typescript-eslint/no-non-null-assertion */
// eslint-disable-next-line import/named
import { constructAsync } from '../../../webmodule-common/other/async-constructor.js';
import { customElement } from 'lit/decorators.js';

import { DataEntryTabManagerView } from '../../../webmodule-common/other/ui/data-entry-screen-base.js';
import { DataProvider } from '../../../webmodule-common/other/data-provider.js';
import { Franchisee, InputUpdateFranchiseeConfiguration } from '../../api/dealer-api-interface-franchisee.js';
import { FranchiseeBrandingView } from './franchisee-branding-view.js';
import { FranchiseeConfigurationView } from './franchisee-configuration-view.js';
import { FranchiseeGuidesView } from './franchisee-guides-view.js';
import { FabricatorSettings, FranchiseeFabricatorSettingsView } from './franchisee-fabricator-settings-view.js';
import { franchiseeDataAdaptor } from '../data/franchisee-data-adaptor.js';
import { getApiFactory } from '../../api/api-injector.js';
import { FranchiseeInformationView } from './franchisee-information-view.js';
import { FranchiseeLaborRateMatrixView } from './franchisee-labor-rate-matrix-view.js';
import { FranchiseeStaffView } from './franchisee-staff-view.js';
import { FranchiseeStockView } from './franchisee-stock-view.js';
import { FranchiseeSupplierSettingsView } from './franchisee-supplier-settings-view.js';
import { TabManager } from '../../../webmodule-common/other/ui/tab-manager/tab-manager.js';
import type { TabPage } from '../../../webmodule-common/other/ui/tab-manager/tab-manager-types.js';
import { Snippet } from '../../../webmodule-common/other/ui/events.js';
import { glossary } from '../../../localization/global.js';
import { LangFranchiseeView } from '../../../localization/scripts/franchisee/view/franchisee-view-lang';
import { userDataStore } from '../../dealer-franchisee/common/current-user-data-store.js';

@customElement('wm-franchiseeview')
export class FranchiseeView extends DataEntryTabManagerView {
  private informationView: FranchiseeInformationView | null = null;
  private staffView: FranchiseeStaffView | null = null;

  private configurationView: FranchiseeConfigurationView | null = null;
  private stockView: FranchiseeStockView | null = null;
  private brandingView: FranchiseeBrandingView | null = null;
  private supplierSettingsView: FranchiseeSupplierSettingsView | null = null;
  private guidesView: FranchiseeGuidesView | null = null;
  private fabricatorSettingsView: FranchiseeFabricatorSettingsView | null = null;
  private laborRateMatrixView: FranchiseeLaborRateMatrixView | null = null;

  franchiseeDataProvider: DataProvider<Franchisee> = new DataProvider<Franchisee>(
    () => franchiseeDataAdaptor().getFranchisee(),
    async franchisee => await franchiseeDataAdaptor().setFranchisee(franchisee)
  );
  franchiseeConfigDataProvider: DataProvider<InputUpdateFranchiseeConfiguration> =
    new DataProvider<InputUpdateFranchiseeConfiguration>(
      () => {
        return franchiseeDataAdaptor().getFranchiseeConfig();
      },
      async (item: InputUpdateFranchiseeConfiguration) =>
        await franchiseeDataAdaptor().setFranchiseeConfig({
          franchiseeConfiguration: item.franchiseeConfiguration,
          taxRates: item.taxRates
        })
    );

  constructor() {
    super();
  }

  protected name(): string {
    return 'franchisee-settings';
  }

  public async prepareForSave(): Promise<void> {
    //DOES NOTHING, HANDLED BY CHILDREN
  }

  public async afterConstruction(): Promise<void> {
    this.configurationView = new FranchiseeConfigurationView(this.franchiseeConfigDataProvider);
    this.brandingView = await constructAsync(new FranchiseeBrandingView(this.franchiseeDataProvider));
    this.stockView = await constructAsync(new FranchiseeStockView());
    this.informationView = await constructAsync(new FranchiseeInformationView());
    this.staffView = await constructAsync(new FranchiseeStaffView());
    this.supplierSettingsView = await constructAsync(new FranchiseeSupplierSettingsView());
    this.guidesView = await constructAsync(new FranchiseeGuidesView());
    if (userDataStore.isFabricatorMode) {
      const fabricatorSettingsDataProvider = new DataProvider<FabricatorSettings>(
        () => {
          const props = userDataStore.getFranchisee().properties;
          return {
            kerfValue: props['optimizer-kerf-value'] ?? '',
            kerfDisplay: props['optimizer-kerf-display'] ?? '',
            kerfAngleValue: props['optimizer-kerf-angle-value'] ?? '',
            kerfAngleDisplay: props['optimizer-kerf-angle-display'] ?? ''
          };
        },
        async (item: FabricatorSettings) => {
          const result = await getApiFactory()
            .franchisee()
            .updateFranchiseePropreties({
              properties: {
                'optimizer-kerf-value': item.kerfValue,
                'optimizer-kerf-display': item.kerfDisplay,
                'optimizer-kerf-angle-value': item.kerfAngleValue,
                'optimizer-kerf-angle-display': item.kerfAngleDisplay
              }
            });
          if (result) {
            userDataStore.applyLocalPropertyChanges({
              'optimizer-kerf-value': item.kerfValue,
              'optimizer-kerf-display': item.kerfDisplay,
              'optimizer-kerf-angle-value': item.kerfAngleValue,
              'optimizer-kerf-angle-display': item.kerfAngleDisplay
            });
          }
          return result ? item : null;
        }
      );
      this.fabricatorSettingsView = new FranchiseeFabricatorSettingsView(fabricatorSettingsDataProvider);
      this.laborRateMatrixView = new FranchiseeLaborRateMatrixView();
    }

    //this creates the tab manager; the per-tab buttons render in the primary
    //(end side) region, matching the legacy right button placement
    await super.afterConstruction();
  }

  /**
   * inherited;
   * @returns
   */
  public internalDataChanged(): boolean {
    return false;
  }

  /**
   * inherited
   * @returns
   */
  public getDataDictionaryName(): string {
    return glossary('franchisee');
  }

  /**
   * inherited
   * @returns
   */
  public isDataReadonly(): boolean {
    return false;
  }

  public getTitle(): Snippet {
    return LangFranchiseeView.settingsTitle();
  }

  protected createTabManager(): TabManager {
    // The settings page renders per-tab buttons bottom-right (its legacy
    // pageButtonLocation was right; approved during the TabManager migration).
    const rightButtons = { buttonRegion: 'primary' as const };
    // build static pages for each of the configured table settings
    const getInitialPages = (): TabPage[] => {
      const pages: TabPage[] = [];
      pages.push(this.informationView!.createTabPage(undefined, rightButtons));
      pages.push(this.configurationView!.createTabPage(undefined, rightButtons));
      pages.push(this.supplierSettingsView!.createTabPage(undefined, rightButtons));
      if (this.stockView && this.stockView.ShowTab) pages.push(this.stockView.createTabPage(undefined, rightButtons));
      if (this.fabricatorSettingsView) pages.push(this.fabricatorSettingsView.createTabPage(undefined, rightButtons));
      if (this.laborRateMatrixView) pages.push(this.laborRateMatrixView.createTabPage(undefined, rightButtons));

      pages.push(this.brandingView!.createTabPage(undefined, rightButtons));

      pages.push(this.staffView!.createTabPage(undefined, rightButtons));

      // Guides sits last: it is reference material rather than settings, so it should not come
      // between two tabs a user is actually there to change.
      pages.push(this.guidesView!.createTabPage(undefined, rightButtons));

      return pages;
    };
    return new TabManager({
      initialIndex: 0,
      pages: () => getInitialPages()
    });
  }
}
